import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactFlow, { Background, Controls, MiniMap } from 'reactflow';
import 'reactflow/dist/style.css';
import Button from '../../components/common/Button';
import { workflowsApi, extractOne } from '../../api/facades';

const WorkflowDiagramPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const numId = id ? parseInt(id, 10) : NaN;

  const { data: vizRes } = useQuery({
    queryKey: ['workflows', numId, 'visualization'],
    queryFn: () => workflowsApi.getVisualization(numId),
    enabled: !Number.isNaN(numId),
  });

  const vizData = vizRes?.data?.data ?? (extractOne(vizRes) as any)?.data;
  const nodesPayload = vizData?.nodes ?? [];
  const edgesPayload = vizData?.edges ?? [];
  const workflowName = vizData?.workflow_name ?? 'Workflow';

  const { nodes, edges } = useMemo(() => {
    const nodes = nodesPayload.map((n: any, i: number) => {
      const x = 100 + (i % 3) * 220;
      const y = 80 + Math.floor(i / 3) * 120;
      const assignees = (n.assignees ?? []).map((a: any) => a.name).join(', ');
      const label = [n.task_subject, n.project_status ?? '—', assignees ? `Assignees: ${assignees}` : ''].filter(Boolean).join(' · ');
      return {
        id: String(n.id),
        type: 'default',
        position: { x, y },
        data: { label },
      };
    });
    const edges = edgesPayload.map((e: any, i: number) => ({
      id: `e-${e.from}-${e.to}-${i}`,
      source: String(e.from),
      target: String(e.to),
    }));
    return { nodes, edges };
  }, [nodesPayload, edgesPayload]);

  if (Number.isNaN(numId)) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
        <p className="text-slate-600">Invalid workflow.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/dashboard/workflows')}>Back</Button>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-slate-700 dark:text-slate-200">Diagram: {workflowName}</h1>
        <Button variant="secondary" onClick={() => navigate(`/dashboard/workflows/${numId}`)}>Back to workflow</Button>
      </div>
      <div className="h-[500px] border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
        <ReactFlow nodes={nodes} edges={edges} fitView>
          <Background />
          <Controls />
          <MiniMap />
        </ReactFlow>
      </div>
    </div>
  );
};

export default WorkflowDiagramPage;
